import { useMemo } from 'react';

import { PermissionRoles } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { GearbeltPlanType } from '@site-mate/sitemate-global-shared/lib';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import clockImage from '@/assets/clock.svg';
import { useToast } from '@/components/ui/toaster';
import { getSitemateStartAccountQueryOptions } from '@/queries/workspace-switcher';
import { useUserStore } from '@/stores/user.store';
import { useWorkspaceSwitcherStore } from '@/stores/workspace-switcher-store';

import SendIcon from '../icons/send';
import SwitchIcon from '../icons/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

function useAccountSubscriptionDetails() {
  const { org } = useUserStore();

  const { data: smsAccount } = useQuery(getSitemateStartAccountQueryOptions({ accountId: org?.accountId }));

  const isFreeTrialExpired = useMemo(() => {
    if (!smsAccount) return false;
    const isFreeTrialPlan = smsAccount.billing?.gearbelt?.planType === GearbeltPlanType.FreeTrial;
    const trialEndDate = new Date(smsAccount.billing?.gearbelt?.trialEndDate as string);
    const now = new Date();
    return isFreeTrialPlan && trialEndDate < now;
  }, [smsAccount]);

  return {
    isFreeTrialExpired,
  };
}

export function ExpiredTrialModal() {
  const { isFreeTrialExpired } = useAccountSubscriptionDetails();
  const { user, setUser, setOrg, hasMultipleWorkspaces } = useUserStore();
  const { setIsOpen } = useWorkspaceSwitcherStore();
  const { signoutRedirect } = useAuth();
  const toast = useToast();

  return (
    isFreeTrialExpired &&
    !user?.roles.includes(PermissionRoles.SitemateSuperUser) && (
      <div className="z-50">
        <Dialog open={isFreeTrialExpired}>
          <DialogContent className="max-w-[500px]">
            <img src={clockImage} alt="clock image" className="m-auto h-[144px] w-[226.47px]" />
            <DialogHeader>
              <DialogTitle hideCloseButton={true} className="text-default-text flex justify-center border-b-0 text-xl leading-normal font-medium">
                Your 30-day free trial of Gearbelt has ended
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="text-default-text flex flex-col items-center gap-4 overflow-hidden leading-normal font-normal text-nowrap">
              <span className="text-center">
                We hope you had a great experience!
                <br />
                Please reach out to us if you would like to subscribe to continue usage.
              </span>
              <a href="mailto:info@sitemate.com?subject=Upgrade%20Subscription" role="button">
                <Button variant="success" className="p-2 text-sm focus-visible:ring-0">
                  <SendIcon className="pr-1" />
                  Email Us
                </Button>
              </a>

              {hasMultipleWorkspaces && (
                <>
                  <span className="text-center">Have access to multiple workspaces?</span>

                  <Button variant="grey" onClick={() => setIsOpen(true)}>
                    <SwitchIcon size={20} className="pr-1" />
                    Switch workspace
                  </Button>
                </>
              )}

              <Button
                variant="link"
                className="p-0 underline"
                onClick={async () => {
                  setUser(null);
                  setOrg(null);

                  await signoutRedirect({
                    post_logout_redirect_uri: window.location.origin,
                  }).catch(() => toast.error('Your request could not be completed. Please try again.'));
                }}>
                Return to login
              </Button>
            </DialogDescription>
          </DialogContent>
        </Dialog>
      </div>
    )
  );
}
